// steps/Step3FetchMeterReadings.tsx
import {
  useMeterReadingSummary,
  useUpdateBillingConfiguration,
} from "@features/bx/BulkBillGeneration/hooks";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import { Label } from "@shared/ui/label";
import { Tabs } from "@shared/ui/tabs";
import {
  AlertCircle,
  AlertTriangle,
  Calculator,
  CheckCircle2,
  Clock,
  FileText,
  Gauge,
  Info,
  Users,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BillDaysThresholdTab } from "./Tabs/BillDaysThreshold/BillDaysThreshold";
import DashboardTab from "./Tabs/DashboardTab/DashboardTab";
import MeterReadingTab from "./Tabs/MeterReadingTab/MeterReadingTab";
import MeterReplacementTab from "./Tabs/MeterReplacement/MeterReplacement";
import { TrueUpTab } from "./Tabs/TrueUpTab/TrueUp";

interface Step3FetchMeterReadingsProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  allStepData?: Record<number, any>;
}

const Step3FetchMeterReadings: React.FC<Step3FetchMeterReadingsProps> = ({
  stepHelpers,
  currentStepIndex = 2,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  clearValidationError,
  isValidating,
  allStepData,
}) => {
  const dataLoadedRef = useRef(false);
  const navigate = useNavigate();
  const { id, cycleId, action } = useParams();
  const [invoiceEndDate, setInvoiceEndDate] = useState<string | null>(null);

  // Fetch meter readings analysis
  const { data: meterReadingSummary } = useMeterReadingSummary(cycleId);

  // Consent checkboxes state — pre-checked when resuming a draft
  const [meterReadingVerified, setMeterReadingVerified] = useState(true);
  const [meterDataVerified, setMeterDataVerified] = useState(true);
  const [autoCreditEnabled, setAutoCreditEnabled] = useState(true);

  // Meter Replacement state
  const [meterReplacementTemplate, setMeterReplacementTemplate] =
    useState<string>("");
  const [meterReplacementSeparateRows, setMeterReplacementSeparateRows] =
    useState<string[]>([]);
  const [meterReplacementCombineRows, setMeterReplacementCombineRows] =
    useState<string[]>([]);

  const { mutateAsync: updateBillingCycle, isPending: isUpdating } =
    useUpdateBillingConfiguration(id);

  // Save step data helper
  const saveStepData = useCallback(
    (stepData: any) => {
      if (!stepHelpers) return;
      try {
        const dataToSave = {
          ...stepData,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, dataToSave);
      } catch (error) {
        console.error("Failed to save step 3 data:", error);
      }
    },
    [stepHelpers, currentStepIndex],
  );

  // Get step data helper
  const getStepData = useCallback(() => {
    if (!stepHelpers) return {};
    try {
      return stepHelpers.getStepData(currentStepIndex) || {};
    } catch (error) {
      console.error("Failed to get step 3 data:", error);
      return {};
    }
  }, [stepHelpers, currentStepIndex]);

  // Handlers for MeterReplacementTab callbacks
  const handleMeterReplacementTemplateChange = useCallback(
    (template: string) => {
      setMeterReplacementTemplate(template);
    },
    [],
  );

  const handleMeterReplacementRowsChange = useCallback(
    (separateRows: string[], combineRows: string[]) => {
      setMeterReplacementSeparateRows(separateRows);
      setMeterReplacementCombineRows(combineRows);
    },
    [],
  );

  // Load saved data on mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData && Object.keys(savedStepData).length > 0) {
          setMeterReadingVerified(savedStepData.meterReadingVerified || false);
          setMeterDataVerified(savedStepData.meterDataVerified || false);
          setMeterReplacementTemplate(
            savedStepData.meterReplacementTemplate || "",
          );
          setMeterReplacementSeparateRows(
            savedStepData.meterReplacementSeparateRows || [],
          );
          setMeterReplacementCombineRows(
            savedStepData.meterReplacementCombineRows || [],
          );
          onDataChange?.(savedStepData);
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step 3 data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, getStepData, onDataChange]);

  // Auto-save when checkboxes or meter replacement data changes
  useEffect(() => {
    if (dataLoadedRef.current) {
      const updatedData = {
        ...data,
        meterReadingVerified,
        meterDataVerified,
        meterReplacementTemplate,
        meterReplacementSeparateRows,
        meterReplacementCombineRows,
        lastUpdated: new Date().toISOString(),
      };
      onDataChange?.(updatedData);
      saveStepData(updatedData);
    }
  }, [
    meterReadingVerified,
    meterDataVerified,
    meterReplacementTemplate,
    meterReplacementSeparateRows,
    meterReplacementCombineRows,
  ]);

  const handleNext = async (isDraft: boolean) => {
    clearValidationError?.();

    // Validate that both checkboxes are checked
    if (!meterReadingVerified || !meterDataVerified) {
      setValidationError?.(
        "Please review and confirm all meter reading details before proceeding.",
      );
      return;
    }

    try {
      const payload = {
        step_3_config: {
          cycle_id: cycleId,
          billing_period_end: invoiceEndDate,
          meter_reading_verified: meterReadingVerified,
          meter_data_verified: meterDataVerified,
          create_credit_note_over_billed: autoCreditEnabled,
          send_alert: true,
          meter_replacement_separate: meterReplacementSeparateRows,
          meter_replacement_combine: meterReplacementCombineRows,
          meter_replacement_template: meterReplacementTemplate,
        },
        step: {
          step_working_on: 2,
          total_steps: 5,
        },
      };

      const response = await updateBillingCycle(payload);
      const completedData = {
        ...data,
        meterReadingVerified,
        meterDataVerified,
        meterReplacementTemplate,
        meterReplacementSeparateRows,
        meterReplacementCombineRows,
        isStepComplete: true,
        completedAt: new Date().toISOString(),
      };

      onDataChange?.(completedData);
      saveStepData(completedData);
      if (isDraft) {
        navigate("/bx/create-bill-generation");
      } else {
        onNext?.();
      }
    } catch (error) {
      console.error("Failed to continue from step 3:", error);
      setValidationError?.(
        "An error occurred while processing. Please try again.",
      );
    }
  };

  const handlePrevious = () => {
    const currentData = {
      ...data,
      meterReadingVerified,
      meterDataVerified,
      meterReplacementTemplate,
      meterReplacementSeparateRows,
      meterReplacementCombineRows,
      navigatedBack: true,
      navigatedBackAt: new Date().toISOString(),
    };
    onDataChange?.(currentData);
    saveStepData(currentData);
    onPrevious?.();
  };

  // KPI Cards data
  const meterAnalysisCards = [
    {
      title: "Total Resources",
      count: meterReadingSummary?.summaryCards?.totalMeters ?? "NA",
      icon: <Gauge className="h-8 w-8 text-blue-500" />,
      trendSummary: "Active resources in cycle",
    },
    {
      title: "Readings Collected",
      count: `${meterReadingSummary?.summaryCards?.readingsCollected}%`,
      icon: <CheckCircle2 className="h-8 w-8 text-green-500" />,
      trendSummary: "Collection completion rate",
    },
    {
      title: "Missing Readings",
      count: meterReadingSummary?.summaryCards?.missingReadings ?? "NA",
      icon: <AlertTriangle className="h-8 w-8 text-orange-500" />,
      trendSummary: "Require attention",
    },
    {
      title: "Pending Validation",
      count: meterReadingSummary?.summaryCards?.pendingValidation ?? "NA",
      icon: <Clock className="h-8 w-8 text-purple-500" />,
      trendSummary: "Awaiting review",
    },
    {
      title: "Total Consumers",
      count: meterReadingSummary?.summaryCards?.totalConsumers ?? "NA",
      icon: <Users className="h-8 w-8 text-blue-500" />,
      trendSummary: "In billing cycle",
    },
    {
      title: "Actual Bills",
      count: meterReadingSummary?.summaryCards?.actualBills ?? "NA",
      icon: <FileText className="h-8 w-8 text-green-500" />,
      trendSummary: "Based on meter readings",
    },
    {
      title: "Estimated Bills",
      count: meterReadingSummary?.summaryCards?.estimatedBills ?? "NA",
      icon: <Calculator className="h-8 w-8 text-purple-500" />,
      trendSummary: "Due to missing readings",
    },
    {
      title: "Unbilled Consumers",
      count: meterReadingSummary?.summaryCards?.unbilledConsumers ?? "NA",
      icon: <AlertCircle className="h-8 w-8 text-red-500" />,
      trendSummary: "Revenue at risk",
    },
  ];

  // Tab components configuration
  const meterReadingTabs = {
    dashboard: {
      label: "Dashboard",
      icon: <Info className="h-4 w-4" />,
      component: <DashboardTab data={data} />,
    },
    "meter-reading": {
      label: "Readings",
      icon: <Gauge className="h-4 w-4" />,
      component: (
        <MeterReadingTab
          data={data}
          summaryData={meterReadingSummary}
          onInvoiceEndChange={setInvoiceEndDate}
        />
      ),
    },
    "meter-replacement": {
      label: "Resource Replacement",
      icon: <AlertTriangle className="h-4 w-4" />,
      component: (
        <MeterReplacementTab
          data={data}
          onTemplateChange={handleMeterReplacementTemplateChange}
          onSelectedRowsChange={handleMeterReplacementRowsChange}
        />
      ),
    },
    "true-up": {
      label: "True-Up",
      icon: <CheckCircle2 className="h-4 w-4" />,
      component: <TrueUpTab />,
    },
    "bill-days-threshold": {
      label: "Bill Days Threshold Check",
      icon: <Clock className="h-4 w-4" />,
      component: (
        <BillDaysThresholdTab
          creditNoteEnabled={autoCreditEnabled}
          onCreditNoteChange={setAutoCreditEnabled}
        />
      ),
    },
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-900">
          Review readings and select which resources to include in the
          billing process. You can filter readings by date, resource type, or
          consumer group.
        </p>
      </div>

      {/* Meter Readings Analysis - KPI Cards */}
      <div>
        <h2 className="text-xl font-semibold mb-2">Readings Analysis</h2>
        <p className="text-sm text-gray-600 mb-4">
          Analyze readings, validate data, and resolve issues
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {meterAnalysisCards.map((card, index) => (
            <QuickStatsCard
              key={index}
              title={card.title}
              count={card.count}
              trendSummary={card.trendSummary}
              icon={card.icon}
            />
          ))}
        </div>
      </div>

      {/* Revenue Leakage Alert */}
      {(meterReadingSummary?.revenueLeakageAlert?.unbilledCount ?? 0) > 0 && (
        <div className="bg-red-50 border-l-4 border-l-red-500 rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-900 mb-1">
                  Revenue Leakage Alert
                </h3>
                <p className="text-sm text-red-800 mb-2 space-y-1">
                  <strong>
                    {meterReadingSummary?.revenueLeakageAlert?.unbilledCount}{" "}
                    unbilled consumers
                  </strong>{" "}
                  detected with missing readings. Estimated revenue at risk:
                  <strong className="text-red-900">
                    {" "}
                    {
                      meterReadingSummary?.revenueLeakageAlert
                        ?.estimatedRevenueAtRisk
                    }{" "}
                  </strong>
                </p>
                <p className="text-xs text-red-700">
                  These consumers cannot be billed until their readings are
                  collected or estimated. Review the{" "}
                  <strong>Readings Tab</strong> to identify and resolve
                  missing readings.
                </p>
              </div>
            </div>
            {/* <Button
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-100 flex-shrink-0"
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              View Issues
            </Button> */}
          </div>
        </div>
      )}

      {/* Tabs Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <Tabs
          tabComponents={meterReadingTabs}
          tabsListClassName="w-full grid grid-cols-5"
          disableUrlSync={true}
        />
      </div>

      {/* Review & Consent - Visible Across All Tabs */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-lg mb-2">Review & Consent</h3>
        <p className="text-sm text-gray-600 mb-4">
          Please review and confirm the reading details before proceeding.
        </p>

        <div className="space-y-4">
          {/* Meter Reading Verified */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="meter-reading-verified"
              checked={meterReadingVerified}
              onCheckedChange={(checked) =>
                setMeterReadingVerified(checked as boolean)
              }
              className="mt-1"
            />
            <div className="flex-1">
              <Label
                htmlFor="meter-reading-verified"
                className="font-medium cursor-pointer"
              >
                 Readings verified
              </Label>
              <p className="text-sm text-gray-600 mt-1">
                I confirm that all readings have been reviewed, validated,
                and are accurate for billing purposes.
              </p>
            </div>
          </div>

          {/* Meter Data Verified */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="meter-data-verified"
              checked={meterDataVerified}
              onCheckedChange={(checked) =>
                setMeterDataVerified(checked as boolean)
              }
              className="mt-1"
            />
            <div className="flex-1">
              <Label
                htmlFor="meter-data-verified"
                className="font-medium cursor-pointer"
              >
                Data verified
              </Label>
              <p className="text-sm text-gray-600 mt-1">
                I confirm that all data, including consumption patterns,
                anomalies, and validation statuses, have been thoroughly
                reviewed and are correct.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200">
        <Button onClick={handlePrevious} variant="outline">
          Previous
        </Button>
        <div className="flex gap-4">
          <Button onClick={() => handleNext(true)}>Save As Draft</Button>
          <Button onClick={() => handleNext(false)}>Next</Button>
        </div>
      </div>
    </div>
  );
};

export default Step3FetchMeterReadings;
